/**
 * WiFiCaptivePortal — PUBLIC guest portal for the FWM conference Wi-Fi.
 *
 * Routes: /wifi/portal (canonical) and /portal/wifi (legacy router redirects).
 * Guests are redirected here by the access point after associating with the SSID.
 *
 *   • Voucher code entry           → POST /api/wifi/captive/redeem
 *   • FWM account sign-in          → POST /api/wifi/captive/member-login
 *   • Live session card + timer    → GET  /api/wifi/captive/session/{id}
 *
 * Branding (name / logo / network) comes from /api/wifi/captive/config.
 * No auth required — the voucher code is itself the secret.
 */
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  Wifi, WifiOff, Clock, Database, Gauge, KeyRound, AlertTriangle, LogIn, Church,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const API = process.env.REACT_APP_BACKEND_URL;

const fmtTime = (s) => {
  const sec = Math.max(0, Number(s || 0));
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const ss = sec % 60;
  return h > 0
    ? `${h}h ${String(m).padStart(2, '0')}m`
    : `${String(m).padStart(2, '0')}m ${String(ss).padStart(2, '0')}s`;
};

const Field = ({ label, ...props }) => (
  <label className="block">
    <span className="text-[10px] uppercase tracking-[0.2em] text-teal-200/80">{label}</span>
    <Input {...props} className={`mt-1 h-11 bg-white/10 border-white/20 text-white placeholder:text-white/40 ${props.className || ''}`} />
  </label>
);

const WiFiCaptivePortal = () => {
  const [params] = useSearchParams();
  const [mode, setMode] = useState('voucher');
  const [code, setCode] = useState((params.get('code') || '').toUpperCase());
  const [device, setDevice] = useState('');
  const [creds, setCreds] = useState({ email: '', password: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [session, setSession] = useState(null);
  const [remaining, setRemaining] = useState(null);
  const [cfg, setCfg] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/wifi/captive/config`)
      .then(({ data }) => setCfg(data))
      .catch(() => setCfg({ app_name: 'Faith World Ministries', ssid: 'FWM-Conference' }));
  }, []);

  // Live poll while a session is open
  useEffect(() => {
    if (!session || session.expired) return undefined;
    let mounted = true;
    const tick = async () => {
      try {
        const { data } = await axios.get(`${API}/api/wifi/captive/session/${session.session_id}`);
        if (!mounted) return;
        if (typeof data.remaining_seconds === 'number') setRemaining(data.remaining_seconds);
        if (data.status === 'expired' || (typeof data.remaining_seconds === 'number' && data.remaining_seconds <= 0)) {
          setSession((s) => ({ ...(s || {}), expired: true }));
        }
      } catch { /* ignore network blips */ }
    };
    tick();
    const id = setInterval(tick, 15000);
    return () => { mounted = false; clearInterval(id); };
  }, [session]);

  // 1-second countdown between polls
  useEffect(() => {
    if (remaining == null) return undefined;
    const id = setInterval(() => setRemaining((r) => (r > 0 ? r - 1 : 0)), 1000);
    return () => clearInterval(id);
  }, [remaining]);

  const onConnected = (data) => {
    setSession(data);
    if (data.expires_at) {
      setRemaining(Math.max(0, Math.floor((new Date(data.expires_at) - Date.now()) / 1000)));
    } else {
      setRemaining(null);
    }
  };

  const redeem = async () => {
    setError(null); setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/api/wifi/captive/redeem`, {
        code: code.trim().toUpperCase(),
        device_name: device || undefined,
      });
      onConnected(data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'That code did not work. Please check it and try again.');
    } finally { setSubmitting(false); }
  };

  const memberLogin = async () => {
    setError(null); setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/api/wifi/captive/member-login`, {
        email: creds.email.trim(), password: creds.password,
        device_name: device || undefined,
      });
      onConnected(data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Sign-in failed. Please try again.');
    } finally { setSubmitting(false); }
  };

  const brandName = cfg?.app_name || 'Faith World Ministries';
  const ssid = session?.ssid || cfg?.ssid || 'FWM-Conference';

  const Shell = ({ children, testid }) => (
    <div className="min-h-screen bg-[#04211f] text-white flex items-center justify-center p-4 sm:p-6"
         data-testid={testid}
         style={{ backgroundImage: 'radial-gradient(120% 80% at 50% 0%, #0d5c52 0%, #04211f 60%)' }}>
      <div className="w-full max-w-sm sm:max-w-md">
        <header className="text-center mb-5">
          {cfg?.logo_url ? (
            <img src={cfg.logo_url} alt={brandName}
                 className="h-14 mx-auto object-contain drop-shadow"
                 data-testid="wifi-portal-logo" />
          ) : (
            <Church className="h-12 w-12 mx-auto text-amber-300" data-testid="wifi-portal-logo" />
          )}
          <div className="mt-3 text-base md:text-lg font-medium tracking-tight">{brandName}</div>
          <div className="text-[11px] uppercase tracking-[0.28em] text-teal-200/70 mt-1">
            {ssid} guest Wi-Fi
          </div>
        </header>
        {children}
        <p className="text-center text-[10px] text-white/35 mt-5">
          Need help? Ask at the registration desk{cfg?.support_email ? ` or email ${cfg.support_email}` : ''}.
        </p>
      </div>
    </div>
  );

  if (session && !session.expired) {
    return (
      <Shell testid="wifi-portal-active">
        <Card className="bg-white/10 backdrop-blur-md border-white/15 text-white">
          <CardContent className="pt-7 pb-7 space-y-5 text-center">
            <Wifi className="h-11 w-11 mx-auto text-emerald-300" />
            <div>
              <div className="text-[11px] uppercase tracking-[0.28em] text-emerald-200">You&apos;re online</div>
              <div className="text-xl font-light mt-1">{session.package_name}</div>
              {session.account_name && (
                <div className="text-xs text-teal-200/80 mt-1">Signed in as {session.account_name}</div>
              )}
            </div>
            <div className="rounded-2xl bg-black/25 border border-white/10 py-4">
              <div className="text-[10px] uppercase tracking-[0.28em] text-teal-200/70">Time remaining</div>
              <div className="text-4xl font-bold tabular-nums mt-1" data-testid="wifi-portal-remaining">
                <Clock className="inline h-6 w-6 mr-2 -mt-1 text-emerald-300" />
                {remaining == null ? 'Unlimited' : fmtTime(remaining)}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs text-teal-100/80">
              {session.speed_down_kbps ? (
                <div className="rounded-lg bg-white/5 p-2">
                  <Gauge className="h-3.5 w-3.5 inline mr-1" />↓ {(session.speed_down_kbps / 1024).toFixed(1)} Mbps
                </div>
              ) : null}
              {session.speed_up_kbps ? (
                <div className="rounded-lg bg-white/5 p-2">
                  <Gauge className="h-3.5 w-3.5 inline mr-1" />↑ {(session.speed_up_kbps / 1024).toFixed(1)} Mbps
                </div>
              ) : null}
              {session.data_mb_cap ? (
                <div className="rounded-lg bg-white/5 p-2 col-span-2">
                  <Database className="h-3.5 w-3.5 inline mr-1" />Up to {session.data_mb_cap} MB
                </div>
              ) : null}
            </div>
            <div className="text-[10px] text-white/30 font-mono">{session.session_id}</div>
          </CardContent>
        </Card>
      </Shell>
    );
  }

  if (session && session.expired) {
    return (
      <Shell testid="wifi-portal-expired">
        <Card className="bg-rose-500/10 border-rose-400/30 text-white">
          <CardContent className="pt-8 pb-8 text-center space-y-4">
            <WifiOff className="h-10 w-10 mx-auto text-rose-300" />
            <div className="text-lg">Your session has ended</div>
            <Button onClick={() => { setSession(null); setCode(''); setRemaining(null); }}
                    className="bg-white text-teal-900 hover:bg-teal-50"
                    data-testid="wifi-portal-restart">
              Use another code
            </Button>
          </CardContent>
        </Card>
      </Shell>
    );
  }

  return (
    <Shell testid="wifi-portal">
      <Card className="bg-white/8 backdrop-blur-md border-white/15 text-white">
        <CardContent className="pt-6 pb-7 space-y-5">
          <p className="text-center text-sm text-teal-100/85" data-testid="wifi-portal-welcome">
            {cfg?.welcome_message || 'Welcome — get online in seconds.'}
          </p>

          <div className="grid grid-cols-2 gap-1 rounded-full bg-black/25 p-1">
            <button type="button" onClick={() => { setMode('voucher'); setError(null); }}
                    className={`rounded-full py-2 text-xs font-medium transition-colors ${mode === 'voucher' ? 'bg-white text-teal-900' : 'text-teal-100/80 hover:text-white'}`}
                    data-testid="wifi-portal-tab-voucher">
              Voucher code
            </button>
            <button type="button" onClick={() => { setMode('account'); setError(null); }}
                    className={`rounded-full py-2 text-xs font-medium transition-colors ${mode === 'account' ? 'bg-white text-teal-900' : 'text-teal-100/80 hover:text-white'}`}
                    data-testid="wifi-portal-tab-account">
              FWM account
            </button>
          </div>

          {mode === 'voucher' ? (
            <div className="space-y-3">
              <label className="block">
                <span className="text-[10px] uppercase tracking-[0.2em] text-teal-200/80">Voucher code</span>
                <Input value={code}
                       onChange={(e) => setCode(e.target.value.toUpperCase())}
                       placeholder="ABCDE12345"
                       inputMode="text"
                       autoCapitalize="characters"
                       autoComplete="off"
                       autoFocus
                       className="mt-1 h-14 bg-white/10 border-white/20 text-white text-center text-2xl font-mono tracking-[0.25em] placeholder:text-white/25 placeholder:tracking-normal placeholder:text-base"
                       data-testid="wifi-portal-code-input" />
              </label>
              <Field label="Device name (optional)" value={device}
                     onChange={(e) => setDevice(e.target.value)}
                     placeholder="Mary's iPhone"
                     data-testid="wifi-portal-device-input" />
              <Button disabled={submitting || !code.trim()} onClick={redeem}
                      className="w-full h-12 bg-amber-400 text-teal-950 hover:bg-amber-300 font-semibold"
                      data-testid="wifi-portal-submit">
                <KeyRound className="h-4 w-4 mr-2" />
                {submitting ? 'Connecting…' : 'Connect'}
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              <Field label="Email" type="email" value={creds.email}
                     onChange={(e) => setCreds({ ...creds, email: e.target.value })}
                     placeholder="you@example.com"
                     autoComplete="username"
                     data-testid="wifi-portal-email-input" />
              <Field label="Password" type="password" value={creds.password}
                     onChange={(e) => setCreds({ ...creds, password: e.target.value })}
                     placeholder="••••••••"
                     autoComplete="current-password"
                     data-testid="wifi-portal-password-input" />
              <Button disabled={submitting || !creds.email.trim() || !creds.password}
                      onClick={memberLogin}
                      className="w-full h-12 bg-white text-teal-900 hover:bg-teal-50 font-semibold"
                      data-testid="wifi-portal-member-submit">
                <LogIn className="h-4 w-4 mr-2" />
                {submitting ? 'Signing in…' : 'Log in with your FWM account'}
              </Button>
              <p className="text-[10px] text-white/40 text-center">
                For registered members and staff who already have Wi-Fi access.
              </p>
            </div>
          )}

          {error && (
            <div className="rounded-xl bg-rose-500/15 border border-rose-400/30 text-rose-100 text-sm p-3 flex items-start gap-2"
                 data-testid="wifi-portal-error">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" /> {error}
            </div>
          )}
        </CardContent>
      </Card>
    </Shell>
  );
};

export default WiFiCaptivePortal;
